import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  Award,
  BookOpen,
  Zap,
  Sparkles,
  Users,
  AlertCircle,
} from 'lucide-react'
import Reveal from '../components/landing/Reveal'
import LoadingSpinner from '../components/LoadingSpinner'
import IntakeBadge, { IntakeEyebrowBadge } from '../components/landing/IntakeBadge'
import IntakeProgramCard from '../components/landing/IntakeProgramCard'
import IntakeChooser from '../components/landing/IntakeChooser'
import { useActiveIntake } from '../hooks/useActiveIntake'
import {
  countOpenPrograms,
  isIntakeOpen,
  statusLabel,
} from '../data/activeIntake'
import { fmtDate, fmtDateLong } from '../components/landing/intakeUi'

// Highlight-card icon mapping — keeps the highlight row visual without
// hard-coding any intake-specific content.
const HIGHLIGHT_ICONS = {
  Duration: Clock,
  Approach: Zap,
  Outcome: Award,
  Level: BookOpen,
  Format: Calendar,
  Seats: Users,
}

/**
 * IntakePage — the intake marketing page, fully driven by the active intake.
 *
 * Every visible intake-specific element (nav label, hero, highlights, section
 * heading, programme list, programme cards, dates, CTAs, status messaging)
 * is derived from the single `activeIntake` object resolved by useActiveIntake.
 *
 * States:
 *   - loading                       → spinner
 *   - no active intake (evergreen)  → polished academy message → /programs
 *   - active intake, no programmes  → "{Intake} programmes are being finalised"
 *                                     + Talk to Admissions / Join waitlist
 *   - active intake with programmes → hero + highlights + programme grid
 *
 * No hard-coded intake copy, course codes, or dates live here. Switching the
 * resolved public intake changes the entire page.
 */
export default function IntakePage() {
  const { intake, openIntakes, fallback, loading } = useActiveIntake()

  useEffect(() => {
    if (!intake) return
    document.title = fallback
      ? 'Programs | Synergy Multimedia Academy'
      : `${intake.name} | Synergy Multimedia Academy`
  }, [intake, fallback])

  if (loading) {
    return (
      <div className="cinema-page min-h-screen flex items-center justify-center">
        <LoadingSpinner label="Loading intake" />
      </div>
    )
  }

  // ─── No active intake → evergreen academy message ───────────────────────
  if (fallback || !intake || intake.status === 'none') {
    return <EvergreenIntakePage />
  }

  // ─── More than one intake open → neutral admissions entry ───────────────
  // The applicant chooses which intake they are applying for; the page makes
  // no assumption about which one they want. With a single open intake the
  // page keeps its existing intake-specific experience below.
  if (openIntakes.length > 1) {
    return <MultiIntakeAdmissionsPage intakes={openIntakes} />
  }

  const openCount = countOpenPrograms(intake)
  const total = intake.programmes.length
  const hasProgrammes = total > 0
  const intakeOpen = isIntakeOpen(intake)

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>{intake.name} | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content={intake.hero?.description || `${intake.name} — Synergy Multimedia Academy.`}
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/intake" />
        <meta property="og:title" content={`${intake.name} | Synergy Multimedia Academy`} />
        <meta
          property="og:description"
          content={intake.hero?.description || `${intake.name} — Synergy Multimedia Academy.`}
        />
        <meta property="og:url" content="https://synergymultimediaacademy.com/intake" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />

      <IntakePageNav intake={intake} fallback={fallback} />

      {/* ─── Hero ──────────────────────────────────────────────────────── */}
      <header className="relative pt-8 pb-10 sm:pt-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-cinema-charcoal/80 via-cinema-black to-cinema-black border-b border-white/5">
        <div className="cinema-glow-orb w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] -top-20 sm:-top-40 right-4 sm:right-10 opacity-30 sm:opacity-40 pointer-events-none" />
        <div className="cinema-glow-orb cinema-glow-orb-blue w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bottom-0 -left-10 sm:-left-20 opacity-20 sm:opacity-30 pointer-events-none" />

        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal className="max-w-3xl">
            <div className="mb-4 sm:mb-6">
              <IntakeEyebrowBadge intake={intake} />
            </div>

            <h1 className="cinema-headline-xl !text-4xl sm:!text-5xl lg:!text-6xl !leading-[1.05] text-white font-light tracking-tight">
              {intake.hero?.headline || intake.name}
            </h1>

            <p className="mt-3 sm:mt-6 cinema-body-lg text-xs sm:text-sm md:text-base text-cinema-soft/80 leading-relaxed max-w-2xl">
              {intake.hero?.description}
            </p>

            {/* Intake date / deadline meta line */}
            {(intake.programmeStart || intake.applicationDeadline) && (
              <p className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs sm:text-sm text-cinema-gold/80 font-sans">
                {intake.programmeStart && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
                    Starts {fmtDateLong(intake.programmeStart)}
                  </span>
                )}
                {intake.applicationDeadline && intake.status !== 'closed' && (
                  <span className="inline-flex items-center gap-1.5">
                    <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                    Apply by {fmtDateLong(intake.applicationDeadline)}
                  </span>
                )}
              </p>
            )}

            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-4">
              <IntakePrimaryCta intake={intake} />
              <IntakeSecondaryCta intake={intake} />
            </div>
          </Reveal>

          {/* ─── Highlight cards ────────────────────────────────────────── */}
          {intake.highlights?.length > 0 && (
            <Reveal delay={100} className="mt-8 sm:mt-10 grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-6 pt-6 sm:pt-8 border-t border-white/10">
              {intake.highlights.map((h) => {
                const Icon = HIGHLIGHT_ICONS[h.label] || Sparkles
                return (
                  <div key={h.label} className="p-3 sm:p-4 rounded-sm bg-white/[0.02] border border-white/5">
                    <div className="flex items-center gap-1.5 sm:gap-2 text-cinema-gold text-xs uppercase tracking-wider font-sans mb-1 font-semibold">
                      <Icon className="w-4 h-4" aria-hidden="true" />
                      <span>{h.label}</span>
                    </div>
                    <p className="font-display text-xl sm:text-2xl md:text-3xl text-white font-light">{h.value}</p>
                    {h.detail && <p className="text-xs text-cinema-muted font-sans mt-0.5">{h.detail}</p>}
                  </div>
                )
              })}
            </Reveal>
          )}
        </div>
      </header>

      {/* ─── Programme catalogue (filtered from active intake) ──────────── */}
      <main id="intake-catalogue" className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-8 sm:py-16 scroll-mt-16 sm:scroll-mt-20">
        {!hasProgrammes ? (
          <IntakeFinalisingState intake={intake} />
        ) : (
          <>
            <Reveal className="mb-6 sm:mb-10">
              <div className="flex items-center gap-3 mb-4">
                <IntakeBadge intake={intake} variant="section" showPulse={intakeOpen} />
              </div>
              <h2 className="font-display text-xl sm:text-2xl text-white font-light">
                Programmes open for <span className="text-cinema-gold">{intake.shortLabel}</span>
              </h2>
              <p className="text-xs sm:text-sm text-cinema-muted mt-2 font-sans">
                {openCount > 0
                  ? `${openCount} programme${openCount === 1 ? '' : 's'} currently open for registration · ${total} total in this intake.`
                  : `Registration for ${intake.shortLabel} opens soon. Explore the lineup and secure your seat.`}
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {intake.programmes.map((programme, index) => (
                <Reveal key={programme.id} delay={index * 50}>
                  <IntakeProgramCard
                    program={programme}
                    intakeLabel={intake.shortLabel}
                    enquiryHref={intake.enquiryHref}
                  />
                </Reveal>
              ))}
            </div>

            {/* Footer CTA — keep the evergreen catalogue reachable */}
            <Reveal delay={120} className="mt-12 sm:mt-16">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/5">
                <p className="text-sm text-cinema-muted font-sans text-center sm:text-left">
                  <Calendar className="inline w-4 h-4 text-cinema-gold/70 mr-1.5 -mt-0.5" aria-hidden="true" />
                  Looking for the full catalogue? Browse every academy programme.
                </p>
                <Link to={intake.programmesPageHref || '/programs'} className="cinema-btn-text group/link text-sm">
                  View All Programs
                  <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </>
        )}
      </main>
    </div>
  )
}

// ─── Sub-components (all driven by the active intake) ───────────────────────

function IntakePageNav({ intake, fallback }) {
  const hasIntake = !fallback && intake && intake.status !== 'none'
  const intakeHref = hasIntake ? (intake.intakePageHref || '/intake') : '/programs'
  return (
    <nav className="cinema-glass-strong sticky top-0 z-40 border-b border-white/5">
      <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex justify-between items-center h-14 sm:h-20">
          <Link
            to="/explore"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2.5 sm:gap-3 group shrink-0"
          >
            <img
              src="/Logos/SMA LOGO  DARK MODE.png"
              alt="Synergy Multimedia Academy logo"
              className="h-8 sm:h-12 transition-transform duration-500 group-hover:scale-105"
            />
            <div>
              <p className="font-display text-xs sm:text-sm tracking-tight leading-tight text-white">
                Synergy Multimedia
              </p>
              <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold font-medium">
                Academy
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-2.5 sm:gap-6">
            <Link
              to="/explore"
              className="inline-flex items-center gap-1.5 sm:gap-2 text-cinema-soft hover:text-cinema-gold text-[0.7rem] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-sans font-medium transition-colors group"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
              <span className="hidden xs:inline sm:inline">Home</span>
            </Link>
            <Link
              to="/programs"
              className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-cinema-muted hover:text-cinema-gold transition-colors font-sans"
            >
              <BookOpen size={14} />
              <span>All Programs</span>
            </Link>
            {hasIntake && (
              <Link
                to={intakeHref}
                className="cinema-btn-primary !px-3.5 sm:!px-5 !py-2 sm:!py-2.5 !min-h-[38px] sm:!min-h-[42px] !text-xs"
              >
                {intake.hero?.primaryCta?.label || 'Apply Now'}
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

/**
 * Primary CTA — state-aware. "Apply now" only when the intake is open or
 * closing-soon. Upcoming intakes show "Register your interest" or
 * "Applications open <date>". Closed intakes show "Join waitlist".
 */
function IntakePrimaryCta({ intake }) {
  const cta = intake.hero?.primaryCta
  const label =
    intake.status === 'upcoming'
      ? intake.registrationStart
        ? `Applications open ${fmtDate(intake.registrationStart)}`
        : 'Register your interest'
      : intake.status === 'closed'
        ? 'Join waitlist'
        : cta?.label || 'Apply Now'
  const href =
    intake.status === 'upcoming'
      ? intake.enquiryHref || '/advisor'
      : intake.status === 'closed'
        ? intake.enquiryHref || '/advisor'
        : cta?.href || '/register'

  return (
    <Link
      to={href}
      className="cinema-btn-primary !w-full sm:!w-auto !px-6 !py-3 !text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2 shadow-lg shadow-cinema-gold/20 cursor-pointer min-h-[44px]"
    >
      <span>{label}</span>
      <ArrowRight className="w-4 h-4" />
    </Link>
  )
}

function IntakeSecondaryCta({ intake }) {
  const cta = intake.hero?.secondaryCta
  if (!cta) return null
  return (
    <Link
      to={cta.href}
      className="cinema-btn-ghost !w-full sm:!w-auto !px-5 !py-3 !text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 border border-white/20 hover:border-cinema-gold/60 cursor-pointer min-h-[44px]"
    >
      <span>{cta.label}</span>
    </Link>
  )
}

/**
 * Empty state — the active intake has no programmes configured yet.
 * Shows "{Intake} programmes are being finalised" with admissions / waitlist
 * CTAs. Never falls back to another intake's programmes.
 */
function IntakeFinalisingState({ intake }) {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <div className="w-20 h-20 mx-auto mb-6 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-cinema-gold/10 to-cinema-gold/20 opacity-40" />
        <div className="relative w-full h-full flex items-center justify-center">
          <Calendar size={40} className="text-cinema-gold" strokeWidth={1.5} />
        </div>
      </div>
      <div className="flex justify-center mb-4">
        <IntakeBadge intake={intake} variant="section" />
      </div>
      <h3 className="text-xl font-display text-white mb-2">
        {intake.shortLabel} programmes are being finalised
      </h3>
      <p className="text-sm text-cinema-muted/70 mb-6 max-w-md mx-auto">
        {intake.name} is open. The full programme lineup, schedule and fees will be
        published shortly. Talk to admissions to get notified the moment applications
        open, or join the waitlist to secure your seat early.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to={intake.enquiryHref || '/advisor'}
          className="cinema-btn-primary inline-flex items-center gap-2"
        >
          <Sparkles size={16} /> Talk to Admissions
        </Link>
        <Link
          to={intake.enquiryHref || '/advisor'}
          className="cinema-btn-ghost inline-flex items-center gap-2"
        >
          Join the waitlist
        </Link>
      </div>
    </div>
  )
}

/**
 * Multi-intake admissions entry — shown when more than one intake is open.
 *
 * Deliberately intake-neutral: the hero speaks for the academy, and the
 * applicant picks the intake they want from IntakeChooser. Nothing here names
 * an intake, a date or a programme type; those all arrive from the server.
 */
function MultiIntakeAdmissionsPage({ intakes }) {
  useEffect(() => {
    document.title = 'Admissions | Synergy Multimedia Academy'
  }, [])

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Admissions | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content="Apply to Synergy Multimedia Academy. Practical, project-led creative training — choose the intake that fits your schedule."
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/intake" />
        <meta property="og:title" content="Admissions | Synergy Multimedia Academy" />
        <meta
          property="og:description"
          content="Practical, project-led creative training. Choose the intake that fits your schedule."
        />
        <meta property="og:url" content="https://synergymultimediaacademy.com/intake" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />

      <IntakePageNav intake={null} fallback />

      <header className="relative pt-8 pb-10 sm:pt-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-cinema-charcoal/80 via-cinema-black to-cinema-black border-b border-white/5">
        <div className="cinema-glow-orb w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] -top-20 sm:-top-40 right-4 sm:right-10 opacity-30 sm:opacity-40 pointer-events-none" />
        <div className="cinema-glow-orb cinema-glow-orb-blue w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bottom-0 -left-10 sm:-left-20 opacity-20 sm:opacity-30 pointer-events-none" />

        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal className="max-w-3xl">
            <span className="cinema-label">Admissions</span>
            <h1 className="cinema-headline-xl mt-6 !text-4xl sm:!text-5xl lg:!text-6xl !leading-[1.05] text-white font-light tracking-tight">
              Build the creative skills your future needs.
            </h1>
            <p className="mt-3 sm:mt-6 cinema-body-lg text-xs sm:text-sm md:text-base text-cinema-soft/80 leading-relaxed max-w-2xl">
              Practical, project-led training for aspiring creatives. Choose the intake that
              fits your schedule and start building real work from day one.
            </p>
          </Reveal>
        </div>
      </header>

      <main className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
        <IntakeChooser intakes={intakes} enquiryHref="/advisor" />
      </main>
    </div>
  )
}

/**
 * Evergreen state — no active intake. Polished academy message directing
 * users to the general /programs catalogue.
 */
function EvergreenIntakePage() {
  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Programs | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content="Explore the full Synergy Multimedia Academy catalogue. Applications for our next intake open soon."
        />
      </Helmet>
      <div className="cinema-grain" aria-hidden="true" />

      <IntakePageNav intake={null} fallback />

      <header className="relative pt-8 pb-10 sm:pt-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-cinema-charcoal/80 via-cinema-black to-cinema-black border-b border-white/5">
        <div className="cinema-glow-orb w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] -top-20 sm:-top-40 right-4 sm:right-10 opacity-30 sm:opacity-40 pointer-events-none" />
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal className="max-w-3xl">
            <span className="cinema-label">Synergy Multimedia Academy</span>
            <h1 className="cinema-headline-xl mt-6 !text-4xl sm:!text-5xl lg:!text-6xl !leading-[1.05] text-white font-light tracking-tight">
              The next intake is being prepared.
            </h1>
            <p className="mt-3 sm:mt-6 cinema-body-lg text-xs sm:text-sm md:text-base text-cinema-soft/80 leading-relaxed max-w-2xl">
              Applications for our next cohort open soon. In the meantime, explore the full
              Synergy Multimedia Academy catalogue — seventeen specialist programmes across
              photography, film, design, animation, AI and social media.
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-4">
              <Link to="/programs" className="cinema-btn-primary !w-full sm:!w-auto !px-6 !py-3 !text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2 min-h-[44px]">
                Explore All Programs
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/advisor" className="cinema-btn-ghost !w-full sm:!w-auto !px-5 !py-3 !text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 border border-white/20 hover:border-cinema-gold/60 min-h-[44px]">
                Talk to Admissions
              </Link>
            </div>
          </Reveal>
        </div>
      </header>

      <main className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-8 sm:py-16">
        <div className="max-w-lg mx-auto text-center py-12">
          <AlertCircle className="w-10 h-10 text-cinema-gold/50 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-sm text-cinema-muted font-sans">
            No intake is currently open for applications. Browse the full catalogue and
            talk to admissions to be notified when the next intake opens.
          </p>
          <Link to="/programs" className="mt-6 cinema-btn-text group/link text-sm inline-flex">
            View All Programs
            <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
          </Link>
        </div>
      </main>
    </div>
  )
}
